import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
    performGetRequest,
    performPostRequest,
    performPutRequest,
    performPatchRequest,
    performDeleteRequest,
} from './apiClient';

/**
 * Custom React Query hook to perform an infinite GET request.
 *
 * @param {Object} config
 * @param {string} config.key - Unique key for the query cache.
 * @param {string} config.url - The endpoint URL (first page).
 * @param {Object} [config.options={}] - Additional options for `useInfiniteQuery`.
 *
 * The API response must include:
 * - `results`: The data array
 * - `next`: The next page URL (or null)
 *
 * @returns {Object} - The result from `useInfiniteQuery`
 */

export const useInfiniteScrollQuery = ({ key, url, params = {}, options = {} }) => {
    return useInfiniteQuery({
        queryKey: [key, params],
        queryFn: async ({ pageParam = 1 }) => {
            const queryParams = new URLSearchParams({ ...params, page: pageParam });
            const response = await performGetRequest({ url: `${url}?${queryParams}` });
            return response.data;
        },
        getNextPageParam: lastPage => {
            const nextPageUrl = lastPage?.next;
            if (!nextPageUrl) return undefined; // no more pages

            const nextPageParam = new URL(nextPageUrl).searchParams.get('page');
            return nextPageParam ? parseInt(nextPageParam, 10) : undefined;
        },
        refetchOnWindowFocus: false,
        retry: false,
        ...options,
    });
};

/**
 * Custom React Query hook to perform a GET request.
 *
 * @param {Object} config - Configuration object for the query.
 * @param {string} config.key - Unique key for the query cache.
 * @param {string} config.url - The endpoint URL to fetch data from.
 * @param {Object} [config.params={}] - Optional query parameters to include in the request.
 * @param {Object} [config.options={}] - Additional options to pass to the `useQuery` hook (e.g., enabled, staleTime, etc.).
 *
 * @returns {Object} - Returns the result from `useQuery`, including status, data, error, etc.
 *
 */

export const useGetQuery = ({ key, url, params = {}, options = {} }) => {
    return useQuery({
        queryKey: [key, params],
        queryFn: () => performGetRequest({ url, params }).then(res => res.data),
        retry: false,
        refetchOnWindowFocus: false,
        ...options,
    });
};

/**
 * Factory to create mutation hooks (e.g., POST, PUT).
 *
 * @param {Function} requestFn - Function to perform the HTTP request.
 * @returns {Function} - Custom mutation hook.
 */

const createMutationHook = requestFn => config => {
    const queryClient = useQueryClient();

    const { url, queryKey, successMessage, errorMessage, onSuccess, onError, ...options } = config;

    return useMutation({
        mutationFn: ({ payload = {}, params = {} }) => requestFn({ url, payload, params }).then(res => res.data),

        onSuccess: (data, variables, context) => {
            if (queryKey) {
                const keys = Array.isArray(queryKey) ? queryKey : [queryKey];
                keys.forEach(key => {
                    if (key) {
                        queryClient.invalidateQueries({
                            predicate: query => query.queryKey[0] === key,
                        });
                    }
                });
            }

            if (successMessage) {
                toast.success(successMessage);
            }

            onSuccess?.(data, variables, context);
        },

        onError: (error, variables, context) => {
            if (errorMessage) {
                toast.error(errorMessage);
            } else {
                toast.error(error?.response?.data?.detail);
            }

            onError?.(error, variables, context);
        },

        ...options,
    });
};

export const usePostMutation = createMutationHook(performPostRequest);

export const usePutMutation = createMutationHook(performPutRequest);

export const usePatchMutation = createMutationHook(performPatchRequest);

export const useDeleteMutation = createMutationHook(performDeleteRequest);
